import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { string } from './proptypes';

const walk = (object, ssr) => {
  if (object._client && ssr) {
    return null;
  }
  const objs = Object.keys(object).map((key) => {
    if (key === '_query') {
      return object[key];
    }
    if (key === '_skip' && object[key]._value) {
      const obj = object[key];
      return {
        [obj._value]: {
          type: 'Boolean',
          _skip: true,
          _value: `Boolean${typeof obj._defaultValue === 'undefined' ? '!' : ''}`,
          _defaultValue: object[key]._defaultValue
        }
      };
    }

    if (key === '_include' && object[key]._value) {

      const obj = object[key];
      return {
        [obj._value]: {
          type: 'Boolean',
          _include: true,
          _value: `Boolean${typeof obj._defaultValue === 'undefined' ? '!' : ''}`,
          _defaultValue: object[key]._defaultValue
        }
      };
    }

    if (typeof object[key] === 'object') {
      return walk(object[key], ssr);
    }
    return null;
  })
    .filter((val) => val)
    .flat();

  if (!objs.length) return {};

  return objs.reduce((acc, cur) => {
    return {
      ...acc,
      ...cur,
    };
  }, {});
};

export const getQueryArgs = ({ attributes = {}, asString = true, ssr = true }) => {
  const args = walk(attributes, ssr);

  if (!asString) return args;

  return Object.keys(args).map((key) => {
    const defaultValue = typeof args[key]._defaultValue !== 'undefined'
      ? `=${args[key]._defaultValue}`
      : '';
    if (args[key]._isArray) {
      const isRequired = args[key]._isRequired ? '!' : '';
      return `$${key}: [${args[key]._children._value}]${isRequired}`;
    }

    return `$${key}:${typeof args[key] === 'object' ? args[key]._value : args[key]}${defaultValue}`;
  }).join(', ');

};

export const getQueryParams = ({ attributes = {} }) => {

  return Object.keys(attributes).map((key) => {
    return `${key}:$${key}`;
  }).join(', ');

};

export const getQueryType = ({ attributes, name }) => {
  if (attributes[name]?._mutation) return 'mutation';
  return 'query';
};

export const getSubQuery = ({
  attributes = {},
  level = 1,
  ssr,
  clientResolver = false,
  skipDirective,
  includeDirective,
  inlineFragment = false,
  namedFragment = false,
  fragmentType = '',
  fragmentAlias = '',
  store = { namedFragmentArr: [], namedFragments: {} }
}) => {
  const spacing = new Array(level + 1).fill('  ').join('');
  const endSpacing = new Array(level).fill('  ').join('');
  const query = Object.keys(attributes)
    .map((domain) => {
      const currentDomainValue = attributes[domain];
      if (currentDomainValue._client && ssr) return '';

      // eslint-disable-next-line max-len
      if (currentDomainValue.type === 'Object'
        && currentDomainValue._isArray
        && currentDomainValue?._children?.type !== 'String'
      ) {
        return getSubQuery({
          attributes: { [domain]: currentDomainValue._children },
          ssr,
          store,
        }).query;
      }

      let queryParam = domain;
      if (currentDomainValue._query) {
        const params = getQueryParams({ attributes: currentDomainValue._query });
        if (params) {
          queryParam = `${domain}(${params})`;
        }
      }

      let skipDirectiveString = skipDirective
        ? ` @skip(if:$${skipDirective._value})`
        : '';

      let includeDirectiveString = includeDirective
        ? ` @include(if:$${includeDirective._value})`
        : '';

      let clientString = clientResolver
        ? ' @client'
        : '';

      // string response or array response with just a string
      if (currentDomainValue?.type === 'String'
        || (currentDomainValue?._children?.type === 'String' && currentDomainValue._isArray)) {
        // eslint-disable-next-line max-len
        return `${spacing}${queryParam}${skipDirectiveString}${includeDirectiveString}${clientString}`;
      }

      const fragmentName = fragmentAlias !== '' ? fragmentAlias : `Core${domain}Fields`;
      const selector = inlineFragment
        ? `${endSpacing}... on ${domain} {`
        // eslint-disable-next-line max-len
        : `${endSpacing}${queryParam}${skipDirectiveString}${includeDirectiveString}${clientString} {`;

      const innerBody = `${Object.keys(currentDomainValue).map((attribute) => {
        if (attribute === '__typename') {
          return spacing + attribute;
        }
        if (/^_/.test(attribute)) {
          return '';
        }

        const currentDomainAttributeValue = currentDomainValue[attribute];
        const suffix = currentDomainAttributeValue._clientResolver
          ? ' @client'
          : '';

        // mixed prop types that get merged must ignore _children
        /*
        pricing: shapeType({
          value: numberType({ float: true })
        }),
        pricing: params(storeId).shape({
          value: numberType({ float: true })
        }),
          */
        if (!currentDomainAttributeValue._isPropType) {
          if (currentDomainAttributeValue.type === 'Object' && !currentDomainAttributeValue._query) {
            // primitive as a propType should not render children in query
            if (currentDomainAttributeValue._children._isPropType) {
              includeDirectiveString = '';
              skipDirectiveString = '';
              // PropTypes could also have skips and include
              if (currentDomainAttributeValue._skip) {
                skipDirectiveString = ` @skip(if:$${currentDomainAttributeValue._skip._value}) `;
              }

              if (currentDomainAttributeValue._include) {
                // `@include(if:$${includeDirective._value})`
                includeDirectiveString = ` @include(if:$${currentDomainAttributeValue._include._value}) `;
              }

              return spacing + attribute + skipDirectiveString + includeDirectiveString + suffix;
            }

            // arrays that are client only should return empty
            if (currentDomainAttributeValue._client && ssr) return '';

            return getSubQuery({
              clientResolver: currentDomainAttributeValue._clientResolver,
              skipDirective: currentDomainAttributeValue._skip,
              includeDirective: currentDomainAttributeValue._include,
              attributes: { [attribute]: currentDomainAttributeValue._children },
              inlineFragment: currentDomainAttributeValue._inlineFragment,
              namedFragment: currentDomainAttributeValue._namedFragment,
              fragmentType: currentDomainAttributeValue?._fragmentType || '',
              fragmentAlias: currentDomainAttributeValue?._fragmentAlias || '',
              ssr,
              level: level + 1,
              store
            }).query;
          }

          /*
            There's a bug where params.shape can have a defined type field and _query
            which causes this code to think it's a merge conflict and drop the type field entirely.
            A legitimate type field in our GQL data is unlikely to be any of the basic GQL types,
            so we can check for that to avoid the false positive.
          */
          const validGQLTypes = ['Object', 'String', 'Int', 'Float', 'Boolean', 'ID', 'JSON'];
          if (currentDomainAttributeValue._query
            && validGQLTypes.includes(currentDomainAttributeValue.type)) {
            // eslint-disable-next-line max-len
            console.warn(`Warning: a merge conflict has occurred between data sources at ${domain}/${attribute}. The children and type have been removed`, currentDomainAttributeValue);
            // eslint-disable-next-line
            delete currentDomainAttributeValue._children;
            // eslint-disable-next-line
            delete currentDomainAttributeValue.type;
          }
          return getSubQuery({
            attributes: { [attribute]: currentDomainAttributeValue },
            skipDirective: currentDomainAttributeValue._skip,
            includeDirective: currentDomainAttributeValue._include,
            inlineFragment: currentDomainAttributeValue._inlineFragment,
            namedFragment: currentDomainAttributeValue._namedFragment,
            fragmentType: currentDomainAttributeValue?._fragmentType || '',
            fragmentAlias: currentDomainAttributeValue?._fragmentAlias || '',
            ssr,
            store
          }).query;
        }

        // reset includeDirectiveString
        includeDirectiveString = '';
        skipDirectiveString = '';
        // PropTypes could also have skips and include
        if (currentDomainAttributeValue._skip) {
          skipDirectiveString = ` @skip(if:$${currentDomainAttributeValue._skip._value}) `;
        }

        if (currentDomainAttributeValue._include) {
          // `@include(if:$${includeDirective._value})`
          includeDirectiveString = ` @include(if:$${currentDomainAttributeValue._include._value}) `;
        }
        return spacing + attribute + skipDirectiveString + includeDirectiveString + suffix;
      }).filter((item) => {
        return item && item !== '';
      }).join('\n')}`;

      /* eslint-disable no-param-reassign */
      if (namedFragment) {
        if (!store.namedFragments[fragmentType + fragmentName]) {
          store.namedFragments[fragmentType + fragmentName] = `
fragment ${fragmentName} on ${fragmentType} {
  ${innerBody}
}
`;
          store.namedFragmentArr.push(store.namedFragments[fragmentType + fragmentName]);
        }
      }
      /* eslint-enable no-param-reassign */
      return `${selector}
${namedFragment ? '...' + fragmentName : innerBody}
${endSpacing}}`;
    }).join('');

  return { query, store };
};

export const getQueryFromAttributes = ({ name, attributes = {}, level = 1, ssr = true }) => {
  // if (attributes.product?._query) {
  //   // eslint-disable-next-line no-param-reassign
  //   attributes.product._query.dataSource = string();
  // }
  const { query, store } = getSubQuery({
    attributes,
    level,
    ssr
  });

  const args = getQueryArgs({ attributes, ssr });
  const queryType = getQueryType({ attributes, name });

  const argsWrapper = args
    ? `(${args})`
    : '';

  return gql`
${queryType} ${name}${argsWrapper} {
${query}
}
${store.namedFragmentArr.map((fragment) => {
    return fragment;
  })}`;
};
